import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { hours as hrs } from '../lib/hours';
import HoursApprovalDetail from './HoursApprovalDetail';
import './HoursApprovalQueue.css';
import RequesterName from './RequesterName';
import { money } from '../lib/money';
import { rowNoRate } from '../lib/contracts';
import { describeDegraded } from '../lib/apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const day = (v) => (v ? String(v).slice(0, 10) : '—');

/**
 * Over the hours that were authorised for this job.
 *
 * A flag, never a block: the work has already been done by the time anyone
 * sees this, and the approver can already send the hours back with a reason if
 * they disagree. Refusing to display them would not un-work them.
 */
const overBudget = (r) => Number(r.allocated_hours) > 0
  && Number(r.hours_claimed) > Number(r.allocated_hours);

/**
 * Hours waiting for this person to sign off.
 *
 * Distinct from the queue above it, which approves a ticket before any work
 * starts. This one comes after: a contractor has finished, logged their time
 * and submitted it, and the ticket is frozen until somebody here agrees to the
 * figure or sends it back. Nothing can be invoiced until they do.
 *
 * Only what is this person's appears - a request named to another approver is
 * not theirs to act on, so it is not shown. An administrator additionally sees
 * the ones nobody was named to approve.
 */
export default function HoursApprovalQueue() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, hours: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [refusing, setRefusing] = useState(null);
  const [reason, setReason] = useState('');
  // The request whose detail is open. A total is enough to wave a job through
  // and not enough to question it, so opening one is a click away rather than
  // a page away.
  const [opened, setOpened] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/approvals/hours`);
      // The server answers 200 with an empty list when the query itself failed,
      // marking it with this header rather than returning an error. Without
      // reading it, a broken queue and an empty one are the same screen - and
      // the empty one says "nothing is waiting for you", which is how hours
      // that really are waiting get left unapproved and unbilled.
      const degraded = res.headers?.['x-data-degraded'];
      if (degraded) {
        setRows([]);
        setError(`The hours awaiting approval could not be read (${describeDegraded(degraded)}) `
          + 'This is not an empty queue - there may be hours waiting.');
        return;
      }
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setTotals({ total: res.data?.total || 0, hours: res.data?.hours || 0 });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the hours awaiting approval');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action, body) => {
    setBusy(row.id);
    setError(null);
    setNotice(null);
    try {
      const res = await axios.post(`${API_BASE}/approvals/hours/${row.id}/${action}`, body);
      setNotice(action === 'approve'
        ? `${row.ticket_number}: ${hrs(row.hours_claimed)}`
          + (Number(row.expenses_claimed) > 0
            ? ` and ${money(Number(row.expenses_claimed))} of expenses` : '')
          + ' approved. It can now be invoiced.'
        : `${row.ticket_number} sent back to ${res.data?.status || 'In Progress'} — `
          + 'the contractor can amend it and resubmit.');
      setRefusing(null);
      setReason('');
      // Deciding from inside the detail leaves nothing to look at, so it
      // closes on success - and stays open on failure, where the reason for
      // the failure is the thing worth reading.
      setOpened(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  const value = useMemo(
    // Hours at rate plus expenses at cost: what signing off this queue commits
    // the firm to. Leaving the expenses out understated it.
    () => rows.reduce((s, r) => s + r.hours_claimed * Number(r.hourly_rate || 0)
      + Number(r.expenses_claimed || 0), 0),
    [rows]);

  if (loading) return <p className="hq-quiet">Loading hours awaiting approval&hellip;</p>;

  return (
    <div className="hq">
      <div className="hq-head">
        <h2>Hours awaiting your sign-off</h2>
        {rows.length > 0 && (
          <span className="hq-summary">
            {totals.total} ticket{totals.total === 1 ? '' : 's'} · {hrs(totals.hours)} · {money(value)}
          </span>
        )}
      </div>

      {error && <p className="hq-error" role="alert">{error}</p>}
      {notice && <p className="hq-ok" role="status">{notice}</p>}

      {/* An empty list after a failed read is not an empty queue, and saying
          "nothing is waiting for you" underneath an error message is how an
          approver closes the tab on work that is actually sitting there. The
          hours stay unapproved, nothing can be invoiced, and nobody is looking
          for it. When the read failed, the error is the whole answer. */}
      {rows.length === 0 && !error ? (
        <p className="hq-quiet">
          Nothing is waiting for you. When a contractor finishes a job and submits
          their time, it appears here — and stays locked until you agree the hours
          or send it back.
        </p>
      ) : rows.length === 0 ? null : (
        <ul className="hq-list">
          {rows.map((r) => (
            <li key={r.id} className="hq-item">
              <div className="hq-main">
                {/* A button rather than a div with onClick: an approver on a
                    keyboard has to be able to open this, and a div cannot be
                    tabbed to or pressed with Enter. */}
                <button type="button" className="hq-title is-open"
                        onClick={() => setOpened(r)}
                        aria-label={`Review ${r.ticket_number}: ${r.title}`}>
                  <span className="hq-ticket">{r.ticket_number}</span>
                  <strong>{r.title}</strong>
                  <span className="hq-review">Review</span>
                </button>
                <p className="hq-meta">
                  {r.contractor_name}
                  {r.contractor_company ? ` (${r.contractor_company})` : ''}
                  {' · raised by '}<RequesterName ticket={r} fallback="unknown" />
                  {r.category ? ` · ${r.category}` : ''}
                  {' · submitted '}{day(r.requested_at)}
                  {!r.approver_id && <span className="hq-unassigned"> · no named approver</span>}
                  {/* Here because its approver is away and this person
                      covers (113): signed off on their behalf. */}
                  {r.covering && (
                    <span className="hq-covering" data-covering={r.id}> · for {r.approver_name}, who is away</span>
                  )}
                  {/* Gordon's fifth point, 5 October: flagged, not blocked.
                      Somebody who fixes tickets and approves hours will
                      sometimes be the only approver available, and refusing
                      it outright would leave their hours stuck for ever. So
                      it is said plainly on the row and in the ticket's
                      history, which is what an audit asks for. */}
                  {r.self_approval && (
                    <span className="hq-self"> · their own work</span>
                  )}
                  {r.contractor_invoices === false && (
                    <span className="hq-internal"> · internal, costed not invoiced</span>
                  )}
                </p>
                {r.description && <p className="hq-desc">{r.description}</p>}
              </div>

              <div className="hq-figures">
                <span className={overBudget(r) ? 'hq-hours is-over' : 'hq-hours'}>
                  {hrs(r.hours_claimed)}
                </span>
                {/* What was authorised, next to what is being claimed. The
                    approver agreed to the budget at approval, possibly weeks
                    ago; making them remember it is how an over-run gets waved
                    through. No budget was set on plenty of tickets, and that
                    is shown as nothing rather than as zero. */}
                {r.allocated_hours > 0 && (
                  <span className="hq-budget">of {hrs(r.allocated_hours)} budgeted</span>
                )}
                {overBudget(r) && (
                  <span className="hq-over">
                    {hrs(r.hours_claimed - r.allocated_hours)} over
                  </span>
                )}
                {Number(r.hourly_rate) > 0 && (
                  <span className="hq-value">{money(r.hours_claimed * Number(r.hourly_rate))}</span>
                )}
                {/* The contract covers the day but has no rate for it, so
                    the value is at the contractor's own rate (120, 3 Nov). */}
                {rowNoRate(r, r.contractor_company || r.contractor_name) && (
                  <span className="hq-norate" role="note" data-testid="no-contract-rate">
                    {rowNoRate(r, r.contractor_company || r.contractor_name)}
                  </span>
                )}
                {/* Expenses go up with the hours and are decided in the same
                    click, so the approver has to see them before clicking. A
                    receipt is optional by design - which only works if its
                    absence is put in front of the person deciding. */}
                {Number(r.expenses_claimed) > 0 && (
                  <span className="hq-expenses">
                    + {money(Number(r.expenses_claimed))} expenses
                    {r.expense_count > 0 && (
                      <span className="hq-expense-note">
                        {' '}({r.expense_count} item{r.expense_count === 1 ? '' : 's'}
                        {r.expenses_without_receipt > 0
                          ? `, ${r.expenses_without_receipt} without a receipt`
                          : ', all with receipts'})
                      </span>
                    )}
                  </span>
                )}
              </div>

              {refusing === r.id ? (
                <form
                  className="hq-refusal"
                  onSubmit={(e) => { e.preventDefault(); decide(r, 'refuse', { reason }); }}
                >
                  <label htmlFor={`why-${r.id}`}>
                    Why is this going back? The contractor sees this.
                  </label>
                  <textarea
                    id={`why-${r.id}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Four hours is too long for a paper jam — please recheck."
                    rows={2}
                  />
                  <div className="hq-actions">
                    <button type="submit" className="hq-btn is-danger"
                            disabled={busy === r.id || !reason.trim()}>
                      {busy === r.id ? 'Sending…' : 'Send back'}
                    </button>
                    <button type="button" className="hq-btn"
                            onClick={() => { setRefusing(null); setReason(''); }}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="hq-actions">
                  <button type="button" className="hq-btn is-primary" disabled={busy === r.id}
                          onClick={() => decide(r, 'approve')}>
                    {busy === r.id ? 'Approving…' : `Approve ${hrs(r.hours_claimed)}`}
                  </button>
                  <button type="button" className="hq-btn" disabled={busy === r.id}
                          onClick={() => { setRefusing(r.id); setReason(''); }}>
                    Send back
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {opened && (
        <HoursApprovalDetail
          request={opened}
          busy={busy === opened.id}
          onClose={() => setOpened(null)}
          onApprove={(row) => decide(row, 'approve')}
          onRefuse={(row, why) => decide(row, 'refuse', { reason: why })}
        />
      )}
    </div>
  );
}
